<script lang="ts">
  import {sizes} from '@/components/templates-grid/sizes';
  import {TemplatesService} from '@/services';
  import type {GridConfig, PreviewImage} from '@/types/common';
  import type {Shapes, Tags} from '@/types/filters';
  import {onDestroy, onMount} from 'svelte/internal';
  import Swal from 'sweetalert2';
  import tippy from 'tippy.js';
  import {setAnimationControllerLoading} from '../../../components/animations/AnimationController.svelte';
  import Icon from '../../../components/icon/Icon.svelte';
  import {getInstance} from '../../../services/animation';
  import {preview, project, templates} from '../../../store';
  import type {Template} from '../../../types/project';
  import editorInstance from '../editor';
  import editorStore from '../editor.store';

  export const title = 'Templates';

  const shapes = ['Vertical', 'Horizontal'];

  let gridProps: GridConfig = {
    images: [],
    loading: true,
    columnCount: 4
  };
  let isOpen = false;
  // For Filtering
  let filterShape: Shapes = '';
  let filterTags: Tags[] = [];
  let filterFavorites: boolean = false;
  let activeFilter: Shapes = '';

  let page: number = 1;
  let limit: number = 16;
  let selectedTags: string = '';
  let selectedShape: Shapes = '';
  let favorites: boolean = false;
  let loadMore: Boolean = false;
  let fullSidebar: boolean = false;
  let fullHeight: boolean = false;

  let favoriteTooltip: HTMLElement;
  let unFavoriteTooltip: HTMLElement;

  let blankShapes = sizes;

  const addGridProps = (filtered: Template[]) => {
    const images = filtered.map(template => {
      let obj: PreviewImage = {
        id: template.id,
        src: template.preview,
        title: template.title,
        content: template.content,
        isFeatured: template.isFeatured,
        isFavorite: template.isFavorite,
        isFree: template.isFree,
        devalueLevel: template.devalueLevel
      };

      return obj;
    });

    gridProps = {
      ...gridProps,
      loading: false,
      images: images
    };
  };

  const unsubscribeToTemplate = templates.subscribe(t => {
    addGridProps(t.items);
  });

  onMount(async () => {
    await templates.getTemplates(page, limit, selectedShape, selectedTags, favorites, false).then(() => {
      gridProps.loading = false;
      gridProps = gridProps;
    });
  });

  onDestroy(() => {
    unsubscribeToTemplate();
  });

  let changeTemplateId = -1;

  const changeTemplate = async (image: PreviewImage) => {
    let template: any;
    if (image?.type === 'default') {
      template = image;
    } else {
      template = (await templates.getTemplate(image.id)) as Template;
    }
    //const template = (await templates.getTemplate(image.id)) as Template;
    image.content = template?.content;
    changeTemplateId = changeTemplateId++;
    let currentTemplateId = changeTemplateId;

    //console.log("Changing templates...");
    setAnimationControllerLoading(true);
    //editorStore.setActiveComponent(null);
    const instance = getInstance();
    editorStore.setActiveComponent(null);
    // Stop/Pause playing before changing the template
    instance.pause();

    //previewAreaLoadingStore.set(true);
    project.selectTemplate(image as unknown as Template);
    await instance.buildTemplate(image.content, true);
    window.dispatchEvent(new Event('reset-timeline'));
    window.dispatchEvent(new Event('reset-download'));
    preview.set({
      checkIfSermonTitleExist: instance.checkForSermonTitle(),
      checkIfBrandLogoExists: instance.checkForBrandLogo()
    });

    // We need to give it some time to make sure that the template
    const tryToBuild: any = (retries = 0) =>
      setTimeout(async () => {
        changeTemplateId = -1;

        instance.reattachVideo();
        instance.templateLoader.initializeLoaders();

        try {
          // Check to see if we can fetch dimensions
          const dimensions = instance.templateLoader.templateMetadata.captionDimensions;
        } catch (err) {
          console.warn('err', err);
          if (retries < 10) {
            return tryToBuild(retries + 1);
          } else {
            alert('There may have been an issue loading the template. Please try again.');
          }
        }
        instance.buildAnimations(true);
        instance.rebuildChanges();
        editorInstance.reinitialize();
      }, 500);

    tryToBuild();
  };

  const appendTemplates = async () => {
    if (page != 1) {
      await templates.getTemplates(page, limit, selectedShape, selectedTags, favorites, false);
    }
  };

  const checkScrollEvent = (e: Event) => {
    const target = e.target as Element;
    const {scrollHeight, scrollTop, clientHeight} = target;

    loadMore = Math.abs(scrollHeight - scrollTop - 2) <= Math.abs(clientHeight);
  };

  const setActiveFilter = (shape: Shapes | string) => {
    activeFilter = shape as Shapes;

    if (filterShape === activeFilter) {
      filterShape = 'All';
    } else {
      filterShape = activeFilter;
    }
  };

  const filter = async (shape: Shapes, tags: Tags[], favorite: boolean) => {
    page = 1;
    let changed = false;

    if (favorite != favorites) {
      changed = true;
      favorites = favorite;
    }

    if (tags.length == 0 && selectedTags != '') {
      changed = true;
    }

    if (shape == 'All') {
      selectedShape = '';
    } else {
      selectedShape = shape;
    }

    if (tags.length >= 1) {
      selectedTags = tags.join(',');
    } else {
      selectedTags = '';
    }

    if (selectedTags.length != 0 || changed == true || selectedShape != '' || selectedTags != '' || shape == 'All') {
      gridProps.loading = true;
      templates.set({items: []});

      await templates.getTemplates(page, limit, selectedShape, selectedTags, favorites, true);
    }
  };

  $: if (loadMore) {
    page = page + 1;
    appendTemplates();
  }

  $: filter(filterShape, filterTags, filterFavorites);

  $: {
    if (favoriteTooltip) {
      tippy(favoriteTooltip, {
        theme: 'light',
        content: `<div class="flex flex-col p-2 items-center justify-center">
            <span class="inline-block align-middle font-light">Add to Favorites</span>
            </div>`,
        allowHTML: true,
        placement: 'right'
      });
    }
    if (unFavoriteTooltip) {
      tippy(unFavoriteTooltip, {
        theme: 'light',
        content: `<div class="flex flex-col p-2 items-center justify-center">
            <span class="inline-block align-middle font-light">Remove from Favorites</span>
            </div>`,
        allowHTML: true,
        placement: 'right'
      });
    }
  }

  const getHContent = () => {
    if (fullHeight) return 'h-full';
    else if (fullSidebar) return 'h-content-no-banner';
    else return 'h-content';
  };

  const toggleFavorite = async (item: PreviewImage, index: number) => {
    const result = await new TemplatesService().setFavorite(item.id);

    if (result) {
      Swal.fire({
        position: 'bottom-end',
        icon: 'success',
        title: 'Template Favorites Updated',
        showConfirmButton: false,
        timer: 1500,
        toast: true
      });
      gridProps.images[index].isFavorite = !item.isFavorite;
    }
  };
</script>

<div class="flex h-full flex-col bg-white">
  <div class="mx-6 flex flex-row">
    <p class="grow text-base font-bold text-black">Change Template</p>
    <details bind:open={isOpen}>
      <summary class={`flex h-8 w-8 cursor-pointer items-center justify-center rounded bg-gray-200 `}>
        <i class="fa-regular fa-sliders-simple text-gray-700"></i>
      </summary>
      <ul
        class="absolute right-6 z-30 mt-2 flex w-72 flex-col overflow-hidden rounded border border-gray-200 bg-white p-[10px] text-base font-semibold text-gray-600 shadow-xl disabled:bg-gray-400"
      >
        {#each shapes as shape}
          <button
            class={`flex h-11 cursor-pointer flex-row items-center   pl-4 hover:bg-gray-50 ${shape === selectedShape ? 'bg-gray-50' : 'bg-white'} `}
            on:click={() => setActiveFilter(shape)}
          >
            <Icon name={shape} className="w-8 h-8" />
            <span class="text-base font-semibold">{shape}</span>
          </button>
        {/each}
        <hr />
        <button
          class={`flex h-11 flex-row items-center pl-4 ${filterFavorites ? 'bg-gray-50' : 'bg-white'} cursor-pointer hover:bg-gray-50`}
          on:click={() => (filterFavorites = !filterFavorites)}
        >
          <Icon name="Star" className="w-8 px-1 h-8" />
          <span class={`text-base font-semibold `}>Favorites</span>
        </button>
      </ul>
    </details>
  </div>
  <!-- <div class="flex-grow" on:scroll={checkScrollEvent}> -->
  <div
    class="relative grid h-full grid-cols-2 gap-5 overflow-auto px-14 pt-6 {getHContent()}"
    on:scroll={checkScrollEvent}
  >
    <div class="flex flex-col">
      <div class="ratio169 border-1 flex flex-col gap-3 rounded-xl border border-zinc-200 px-2 py-6">
        <span class="text-md flex justify-center text-center">Create From Scratch</span>
        <div class="flex w-full flex-col justify-center gap-4">
          {#each blankShapes as dimension}
            <div
              class=""
              on:click={() => {
                changeTemplate(dimension);
              }}
              on:keypress={() => {
                changeTemplate(dimension);
              }}
            >
              <div class="flex cursor-pointer flex-col items-center justify-center gap-1">
                <div
                  class={`icon-container flex items-center justify-center bg-gray-100 text-gray-400 hover:bg-gray-400 hover:text-white`}
                >
                  <Icon name={dimension.shape} className={`w-10 h-10`} />
                </div>
              </div>
              <p class="mt-2 text-center text-xs">{dimension.shape}</p>
            </div>
          {/each}
        </div>
      </div>
    </div>
    {#each gridProps.images as item, i (i)}
      <div class="">
        <div class="relative aspect-auto w-full" data-template-id={item.id}>
          <button class="w-full" on:click={() => changeTemplate(item)}>
            <img
              class="aspect-auto w-full rounded-lg border border-slate-800 bg-gray-800 shadow-md"
              src={item.src}
              alt={item.title}
            />
          </button>
          <button
            on:click={() => toggleFavorite(item, i)}
            class="absolute right-1 top-1 z-20 rounded-full border-0 p-2"
          >
            {#if item.isFavorite == false}
              <span bind:this={favoriteTooltip}>
                <i class="fa-regular fa-star text-2xl text-[#FABE24]" />
              </span>
            {:else}
              <span bind:this={unFavoriteTooltip}>
                <i class="fa-solid fa-star text-2xl text-[#FABE24]" />
              </span>
            {/if}
          </button>
        </div>
      </div>
    {/each}
  </div>
  <!-- </div> -->
</div>

<style lang="postcss">
  .h-content-no-banner {
    height: calc(100vh - 60px);
    height: calc(100svh - 60px);
  }
  .h-content {
    height: calc(100vh - (60px + 72px));
    height: calc(100svh - (60px + 72px));
  }
  ::-webkit-scrollbar {
    @apply w-2 rounded-full bg-slate-100 p-1;
  }
  ::-webkit-scrollbar-track {
    @apply rounded-full shadow-gray-500;
  }
  ::-webkit-scrollbar-thumb {
    @apply rounded-full bg-gray-400;
  }
  ::-webkit-scrollbar-thumb:hover {
    @apply rounded-full bg-gray-500;
  }
  details > summary {
    list-style-type: none;
    outline: none;
    cursor: pointer;
  }

  details > summary::-webkit-details-marker {
    display: none;
  }

  details > summary::before {
    content: '';
  }

  details[open] > summary::before {
    content: '';
  }

  .icon-container {
    @apply flex h-14 w-14 items-center justify-center rounded shadow transition-all;
  }
</style>
